import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { CurrencyFormatterPipe } from '@trademe/tangram';
import { map, take, takeUntil } from 'rxjs/operators';
import { UserFacade } from '@classifieds-sell/state';
import { FeeService } from './fee.service';
import { combineLatest, Observable, Subject } from 'rxjs';
import {
    IFundSource,
    ITopUpAccountResponse,
    PingService
} from '@classifieds-sell/datalayer';
import {
    INotificationOptions,
    NotificationService
} from '@classifieds-sell/trademe-core-ui';
import { IWidgetPayload } from 'libs/trademe-core-ui/src/lib/ping/ping-widget/widget-to-host-message-types';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'tmp-credit-account',
    templateUrl: 'credit-account.component.html',
    styleUrls: ['credit-account.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CreditAccountComponent implements OnInit, OnDestroy {
    @Output() cancelled: EventEmitter<null> = new EventEmitter();
    @Output() creditAdded = new EventEmitter();
    @Output() topupFailed = new EventEmitter();

    amountToTopUp: number;
    minimumTopUpAmount = 1;
    maximumTopUpAmount = 8000;
    memberSummary = this._userFacade.memberSummary$;
    accountStatus = this._userFacade.topUpAccountStatus$;

    submitCard$ = new Subject<number>();
    private _submitId = 0;

    public widgetPayload: IWidgetPayload;

    addCreditIsLoading = false;
    iframeIsLoaded = false;
    useSavedCard = false;

    private _destroyed$ = new Subject<void>();

    constructor(
        private _feeService: FeeService,
        private _currencyFormatter: CurrencyFormatterPipe,
        private _pingService: PingService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _userFacade: UserFacade,
        private _notificationService: NotificationService
    ) {
        this.minimumTopUpAmount = this.amountToTopUp =
            this._feeService.getTopUpRequiredAmount();
    }

    updateAmountToTopUp(amountToTopUp: number) {
        this.amountToTopUp = amountToTopUp;
    }

    ngOnInit(): void {
        combineLatest([
            this._feeService.topUpRequiredAmountChange$,
            this._userFacade.topUpAccountStatus$
        ])
            .pipe(takeUntil(this._destroyed$))
            .subscribe(([topUpRequiredAmount, topUpAccountStatus]) => {
                this.amountToTopUp = topUpRequiredAmount;
                this.minimumTopUpAmount =
                    topUpRequiredAmount ??
                    topUpAccountStatus.minimumTopUpAmount;
            });

        this._userFacade.topUpAccountStatus$.subscribe((status) => {
            this.maximumTopUpAmount = status.maximumTopUpAmount;
        });
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
    }

    get minimumTopUpValidationMessage(): string {
        return `Please enter an amount that is no less than ${this._currencyFormatter.transform(
            this.minimumTopUpAmount
        )}`;
    }

    get maximumTopUpValidationMessage(): string {
        return `The maximum daily limit for top-ups is ${this._currencyFormatter.transform(
            this.maximumTopUpAmount
        )}`;
    }

    hideCreditAccount(): void {
        this.cancelled.emit();
    }

    public onUpdateFromPingWidget(payload: IWidgetPayload) {
        this.widgetPayload = payload;
        if (payload?.requestId) {
            this.attemptAddCreditWidget(true);
        }
    }

    public isWidgetPayloadValid() {
        if (this.widgetPayload) {
            if (
                !(
                    (this.widgetPayload.useBalance &&
                        !this.widgetPayload.fundSourceAmount) ||
                    this.widgetPayload.fundSourceId > 0
                )
            ) {
                return false;
            }

            return true;
        }
        return false;
    }

    private attemptAddCreditWidget(isCallback = false) {
        if (this.isWidgetPayloadValid()) {
            this.AddCreditWidget(this.widgetPayload);
            return;
        } else {
            // this cancels the loading state on an failed card add
            if (isCallback) {
                this.addCreditIsLoading = false;
            } else {
                this._submitId++;
                this.submitCard$.next(this._submitId);
            }
        }
    }

    processAddCredit(valid: boolean): void {
        this.addCreditIsLoading = true;
        if (!valid || !this.amountToTopUp) {
            this.addCreditIsLoading = false;
            return;
        }

        if (this.widgetPayload) {
            this.attemptAddCreditWidget();
        } else {
            if (!this.iframeIsLoaded) {
                this.addCreditIsLoading = false;
                return;
            }

            this._submitId++;
            this.submitCard$.next(this._submitId);
        }
    }

    onCCIFrameLoaded(): void {
        this.iframeIsLoaded = true;
        this._changeDetectorRef.markForCheck();
    }

    public AddCreditWidget(payload: IWidgetPayload) {
        this.addCreditIsLoading = true;
        this._pingService
            .addCreditPaymentWithDetails(this.amountToTopUp, payload)
            .pipe(take(1))
            .subscribe(
                (result: ITopUpAccountResponse) => {
                    if (result.success) {
                        this.creditAdded.emit();
                    } else {
                        this.handleSubmitError(Error(result.description));
                    }
                },
                (error) => {
                    this.handleSubmitError(error);
                },
                () => {
                    this._changeDetectorRef.markForCheck();
                }
            );
    }

    private handleSubmitError(_error: Error): void {
        this.addCreditIsLoading = false;

        const errorNotification: INotificationOptions = {
            title: 'Top up failed',
            message: _error.message
        };

        this._notificationService.error(errorNotification);
        this._changeDetectorRef.markForCheck();
        this.topupFailed.emit();
    }
}
